/**
 * v-interact directive — Vue 3 drag directive using native Pointer Events.
 * No external dependencies — replaces the archive's interactjs-based directive.
 *
 * Usage:
 *   v-interact:new="{ service }"            — drag a NEW service from the sidebar
 *   v-interact:existing="{ calendarevent }" — drag an EXISTING calendar event
 *
 * On drag-start:
 *   1. Creates a ghost clone floating under the pointer
 *   2. Sets tablesStore.drag so calendar drop zones activate
 *
 * On drag-end:
 *   1. Removes the ghost
 *   2. Resets tablesStore.drag
 */

import type { Directive, DirectiveBinding } from 'vue'
import { useTablesStore } from '@/stores/tables'

export const vInteract: Directive = {
    mounted(el: HTMLElement, binding: DirectiveBinding) {
        let ghost: HTMLElement | null = null
        let startX = 0
        let startY = 0
        let dragging = false
        // Offset from the pointer to the ghost's top-left corner at drag-start.
        // Stored once so the ghost position never needs getBoundingClientRect().
        let shiftX = 0
        let shiftY = 0
        // Whether window pointermove/pointerup listeners are currently registered.
        // Tracked to prevent a re-render race: if Vue unmounts this element while
        // a NEW drag has already registered fresh window listeners, cleanup must
        // NOT remove those listeners (same function refs, different drag session).
        let windowListenersActive = false

        function getDataobj() {
            let dataobj: any = {}
            if (binding.arg === 'existing' && binding.value?.calendarevent) {
                dataobj = { ...binding.value.calendarevent }
            } else if (binding.arg === 'new' && binding.value?.service) {
                dataobj = { ...binding.value.service }
            }
            dataobj.new = binding.arg === 'new'
            return dataobj
        }

        function onPointerDown(ev: PointerEvent) {
            startX = ev.clientX
            startY = ev.clientY
            dragging = false
            if (!windowListenersActive) {
                windowListenersActive = true
                window.addEventListener('pointermove', onPointerMove)
                window.addEventListener('pointerup', onPointerUp)
                window.addEventListener('pointercancel', onPointerCancel)
            }
            // Flag used by calendar drop-zone highlight logic (month view) so the
            // column can be highlighted before the 5px drag threshold is crossed.
            ;(window as any).__vInteractDragging = true
            // Dismiss any open hover cards immediately on press — don't wait for the
            // 5px drag threshold, because the hover card's 400ms timer may already
            // have fired by then.
            window.dispatchEvent(new CustomEvent('vinteract:dragstart'))
        }

        function onPointerMove(ev: PointerEvent) {
            const dx = ev.clientX - startX
            const dy = ev.clientY - startY

            if (!dragging && Math.sqrt(dx * dx + dy * dy) > 5) {
                dragging = true

                // Create ghost
                ghost = el.cloneNode(true) as HTMLElement
                const rect = el.getBoundingClientRect()
                // Capture how far the click was from the element's top-left edge.
                // Using startX/Y (pointerdown position) keeps the same grab-point
                // feel even though the ghost is created a few pixels later.
                shiftX = startX - rect.left
                shiftY = startY - rect.top
                ghost.style.position = 'fixed'
                ghost.style.zIndex = '1060'
                ghost.style.pointerEvents = 'none'
                ghost.style.left = rect.left + 'px'
                ghost.style.top = rect.top + 'px'
                ghost.style.width = rect.width + 'px'
                ghost.style.opacity = '0.85'
                ghost.style.transition = 'none'
                document.body.appendChild(ghost)

                el.style.opacity = '0.3'

                // Activate calendar drop zones
                const tablesStore = useTablesStore()
                const dataobj = getDataobj()
                const dragPayload = {
                    drag: true,
                    draggedelement: {
                        autoopenmodal: binding.arg === 'new',
                        dom: el,
                        dataobj,
                        shiftY: shiftY   // re-use already-computed value
                    }
                }
                ;(tablesStore as any).drag = dragPayload
                // Also persist to window so vDropZone.onPointerUp can read the
                // payload even if vInteract.onPointerUp races ahead and resets
                // tablesStore.drag first. Cleared in a microtask after pointerup.
                ;(window as any).__vInteractLastDrag = dragPayload
            }

            if (dragging && ghost) {
                // Position ghost purely from pointer coords + stored shift.
                // No getBoundingClientRect() here — that value changes when the
                // scroll container scrolls, which would cause the ghost to jump.
                ghost.style.left = (ev.clientX - shiftX) + 'px'
                ghost.style.top  = (ev.clientY - shiftY) + 'px'
            }
        }

        function cleanup(wasDragging: boolean) {
            windowListenersActive = false
            ;(window as any).__vInteractDragging = false
            window.removeEventListener('pointermove', onPointerMove)
            window.removeEventListener('pointerup', onPointerUp)
            window.removeEventListener('pointercancel', onPointerCancel)

            // Remove ghost and restore opacity FIRST — before any async work —
            // so the element is never left in a faded/invisible state even if
            // something downstream throws.
            if (ghost) {
                ghost.remove()
                ghost = null
            }
            el.style.opacity = ''

            if (wasDragging) {
                const tablesStore = useTablesStore()
                    ; (tablesStore as any).drag = { daysdropzone: false }
                dragging = false
                // Clear the last-drag snapshot after all synchronous pointerup
                // listeners (vDropZone cells) have had a chance to read it.
                queueMicrotask(() => { ;(window as any).__vInteractLastDrag = null })
            } else {
                // Plain click or cancel — no drag happened, clear snapshot immediately
                ;(window as any).__vInteractLastDrag = null
            }
        }

        function onPointerUp() {
            cleanup(dragging)
        }

        function onPointerCancel() {
            // Pointer was cancelled by the system (e.g. another touch started,
            // user scrolled with two fingers, or pointer left the browser).
            // Treat as a cancelled drag — restore element without dropping.
            cleanup(false)
        }

        el.addEventListener('pointerdown', onPointerDown)

            // Store cleanup fn on element for unmount.
            // IMPORTANT: only remove window listeners if they are currently active
            // for THIS element. If the element is unmounted during a re-render while
            // a new drag session has already registered listeners (windowListenersActive
            // set by onPointerDown), we must not strip the new session's listeners.
            ; (el as any).__vInteractCleanup = () => {
                el.removeEventListener('pointerdown', onPointerDown)
                if (windowListenersActive) {
                    windowListenersActive = false
                    ;(window as any).__vInteractDragging = false
                    window.removeEventListener('pointermove', onPointerMove)
                    window.removeEventListener('pointerup', onPointerUp)
                    window.removeEventListener('pointercancel', onPointerCancel)
                    ;(window as any).__vInteractLastDrag = null
                    // If unmounted mid-drag, clean up ghost and opacity too
                    if (ghost) { ghost.remove(); ghost = null }
                    el.style.opacity = ''
                }
            }
    },

    unmounted(el: HTMLElement) {
        ; (el as any).__vInteractCleanup?.()
    }
}
